import { useState, useCallback } from 'react';
import http from '@/network/http';

type UploadPurpose = 'profile' | 'message' | 'post' | 'general';

interface UploadResult {
  success: boolean;
  key: string;
  url: string;
  size: number;
  type?: 'image' | 'video' | 'audio' | 'file';
  mimeType?: string;
}

interface UploadState {
  isUploading: boolean;
  progress: number;
  error: string | null;
}

interface PresignResponse {
  uploadUrl: string;
  key: string;
  publicUrl: string;
}

const SIZE_LIMITS: Record<UploadPurpose, number> = {
  profile: 5 * 1024 * 1024, // 5MB
  message: 50 * 1024 * 1024, // 50MB
  post: 50 * 1024 * 1024, // 50MB
  general: 50 * 1024 * 1024, // 50MB
};

const detectFileType = (mimeType: string): 'image' | 'video' | 'audio' | 'file' => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'file';
};

/**
 * Compress an image file using canvas
 * @param file - Original image file
 * @param maxWidth - Maximum width (default: 1920)
 * @param maxHeight - Maximum height (default: 1920)
 * @param quality - WebP quality 0-1 (default: 0.8)
 * @returns Compressed image blob
 */
export const compressImage = async (
  file: File,
  maxWidth = 1920,
  maxHeight = 1920,
  quality = 0.8
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    img.onload = () => {
      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        'image/webp',
        quality
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};

export const useUpload = () => {
  const [state, setState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    error: null,
  });

  const reset = useCallback(() => {
    setState({
      isUploading: false,
      progress: 0,
      error: null,
    });
  }, []);

  const upload = useCallback(
    async (
      file: File,
      purpose: UploadPurpose = 'message',
      shouldCompress = true
    ): Promise<UploadResult | null> => {
      try {
        setState({
          isUploading: true,
          progress: 0,
          error: null,
        });

        // Validate file size
        const sizeLimit = SIZE_LIMITS[purpose];
        if (file.size > sizeLimit) {
          throw new Error(
            `File size exceeds limit of ${Math.floor(sizeLimit / 1024 / 1024)}MB`
          );
        }

        // Compress image if needed
        let fileToUpload: File | Blob = file;
        const fileType = detectFileType(file.type);

        if (shouldCompress && fileType === 'image' && file.type !== 'image/gif') {
          setState((prev) => ({ ...prev, progress: 10 }));
          const compressed = await compressImage(file);
          // Use compressed if it's actually smaller
          if (compressed.size < file.size) {
            fileToUpload = compressed;
          }
        }

        // Step 1: Get presigned URL
        setState((prev) => ({ ...prev, progress: 20 }));
        const presignResponse = await http.post<PresignResponse>(
          '/uploads/presign',
          {
            filename: file.name,
            mimeType: file.type,
            purpose,
          }
        );

        const { uploadUrl, key, publicUrl } = presignResponse.data;

        // Step 2: Upload to R2 via backend (uses axios baseURL for correct origin)
        setState((prev) => ({ ...prev, progress: 30 }));

        await http.put(uploadUrl, fileToUpload, {
          headers: { 'Content-Type': file.type },
        });

        setState((prev) => ({ ...prev, progress: 100 }));

        // Build absolute public URL from the API base
        const apiBase = http.defaults.baseURL || '';
        const absolutePublicUrl = `${apiBase}${publicUrl}`;

        const result: UploadResult = {
          success: true,
          key,
          url: absolutePublicUrl,
          size: fileToUpload.size,
          type: fileType,
          mimeType: file.type,
        };

        setState({
          isUploading: false,
          progress: 100,
          error: null,
        });

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Upload failed';

        setState({
          isUploading: false,
          progress: 0,
          error: errorMessage,
        });

        return null;
      }
    },
    []
  );

  const uploadMultiple = useCallback(
    async (
      files: File[],
      purpose: UploadPurpose = 'message',
      shouldCompress = true
    ): Promise<UploadResult[]> => {
      const results: UploadResult[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const result = await upload(file, purpose, shouldCompress);

        if (result) {
          results.push(result);
        }

        // Update overall progress
        const overallProgress = Math.floor(((i + 1) / files.length) * 100);
        setState((prev) => ({ ...prev, progress: overallProgress }));
      }

      return results;
    },
    [upload]
  );

  return {
    ...state,
    upload,
    uploadMultiple,
    reset,
  };
};
